import { BASE_URL } from 'config';
import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  Button,
  Avatar,
  Typography,
  FormControl,
  FormHelperText,
  Grid,
  Divider,
  Input,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { validateEmail, validateRequired, validatePasswordMatch } from "validations";
import axios from "axios";
import Swal from "sweetalert2";
import avatar from "assets/theme/components/avatar";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";

const AddLoyalty_tiersForm = ({ onClose, onSuccess, initialData }) => {
  const [formData, setFormData] = useState({
    name: initialData?.name || "",
    id: initialData?.id || null,
    min_points:
      initialData?.min_points !== undefined && initialData?.min_points !== null
        ? parseInt(initialData.min_points, 10)
        : null,
    discount_rate: initialData?.discount_rate || null,
  });

  // Add useEffect to handle initial data
  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name,
        id: initialData.id,
        min_points:
          initialData.min_points !== undefined && initialData.min_points !== null
            ? parseInt(initialData.min_points, 10)
            : null,
        discount_rate: initialData.discount_rate,
      });
    }
  }, [initialData]);
  const [errors, setErrors] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false);
  useEffect(() => {
    if (formSubmitted) validateForm();
  }, [formData]);
  const validateForm = () => {
    const newErrors = {};
    if (!validateRequired(formData.name)) newErrors.name = "يجب إدخال الاسم";
    if (!formData.min_points) newErrors.min_points = "يرجى اختيار النقاط الأدنى";
    if (!formData.discount_rate) newErrors.discount_rate = "يرجى اختيار نسبة الخصم";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "min_points" ? (value === "" ? null : parseInt(value, 10)) : value,
    }));
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitted(true);

    if (!validateForm()) return;

    try {
      const formPayload = new FormData();
      // Append all form data including files
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== null && value !== undefined && key !== "id") {
          // Handle avatar differently for existing vs new
          formPayload.append(key, value);
        }
      });
      // Show loading indicator
      Swal.fire({
        title: "جاري الحفظ...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
          const container = document.querySelector(".swal2-container");
          container.style.zIndex = "99999";
        },
      });
      console.log("Sending payload:", formPayload); // طباعة البيانات المرسلة
      const token = localStorage.getItem("user-token");
      const endpoint = formData.id
        ? `${BASE_URL}/administrator/loyalty-tiers/update/${formData.id}`
        : BASE_URL + "/administrator/loyalty-tiers/store";

      const response = await axios.post(endpoint, formPayload, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      // طباعة تفاصيل الرد
      console.log("Full Response:", response);
      console.log("Response Data:", response.data);
      console.log("Status Code:", response.status);
      // Close loading indicator
      Swal.close();

      if (response.data.status) {
        await Swal.fire({
          icon: "success",
          title: "تم الحفظ!",
          text: "تم حفظ البيانات بنجاح",
          confirmButtonText: "موافق",
          didOpen: () => {
            const container = document.querySelector(".swal2-container");
            container.style.zIndex = "99999";
          },
        });
        onSuccess();
        onClose();
      } else {
        throw new Error(response.data.message || "حدث خطأ غير متوقع");
      }
    } catch (error) {
      Swal.close();
      console.error("Error Details:", error);
      console.log("Error Response:", error.response?.data);
      const errorMessage = error.response?.data?.message || error.message || "حدث خطأ أثناء  الحفظ";

      Swal.fire({
        icon: "error",
        title: "فشل الحفظ!",
        text: errorMessage,
        confirmButtonText: "موافق",
        didOpen: () => {
          const container = document.querySelector(".swal2-container");
          container.style.zIndex = "99999";
        },
      });
    } finally {
      setFormSubmitted(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: "",
      min_points: null,
      discount_rate: null,
    });
    setErrors({});
    onClose();
  };

  return (
    <Box
      sx={{
        p: 3,
        width: 600,
        bgcolor: "background.paper",
        borderRadius: 2,
        boxShadow: 3,
      }}
    >
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="bold">
          {formData.id ? "تعديل مستوى الولاء " : "إضافة مستوى ولاء جديد"}
        </Typography>
      </Box>

      {/* Main Form */}
      <Box container spacing={3}>
        {/* Left Column */}
        <Box display="flex" gap={2} sx={{ mb: 2 }}>
          <FormControl fullWidth error={!!errors.name} sx={{ mb: 2 }}>
            <TextField
              label="اسم المستوى *"
              name="name"
              value={formData.name}
              onChange={handleChange}
              error={!!errors.name}
              helperText={errors.name}
            />
          </FormControl>
          <FormControl fullWidth error={!!errors.min_points} sx={{ mb: 2 }}>
            <TextField
              label="ادنى نقاط *"
              name="min_points"
              type="number"
              value={formData.min_points === null ? "" : formData.min_points}
              onChange={handleChange}
              error={!!errors.min_points}
              helperText={errors.min_points}
              inputProps={{ min: 0 }}
            />
          </FormControl>
          <FormControl fullWidth error={!!errors.discount_rate} sx={{ mb: 2 }}>
            <TextField
              label="نسبة الخصم *"
              name="discount_rate"
              type="number"
              value={formData.discount_rate === null ? "" : formData.discount_rate}
              onChange={handleChange}
              error={!!errors.discount_rate}
              helperText={errors.discount_rate}
              inputProps={{ min: 0, max: 100 }}
            />
          </FormControl>
        </Box>
      </Box>
      {/* Actions Section */}
      <Box mt={4} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="textSecondary">
          * الحقول المطلوبة
        </Typography>

        <Box display="flex" gap={2}>
          <Button variant="contained" color="error" onClick={handleCancel} sx={{ px: 4 }}>
            إلغاء
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            // disabled={Object.keys(errors).length > 0}
            sx={{ px: 4, color: "white !important" }}
            onClick={handleSubmit}
          >
            حفظ
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AddLoyalty_tiersForm;
